import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, CornerDownLeft } from 'lucide-react';

// v1.239.0 - "Search pages" (Ctrl/⌘ + K). Jump to any page the user can open by typing a few
// letters. `pages` = [{ path, label, Icon, group }], already filtered by access by the caller.
// Matching is forgiving: every word typed must appear in the label or its group name, so "ship c"
// finds "Shipping Cost" and "pay rem" finds "Remittance" (under Payments).
export default function NavSearch({ open, onClose, pages, onGo }) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => { if (open) { setQ(''); setSel(0); setTimeout(() => inputRef.current?.focus(), 0); } }, [open]);

  const results = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return pages;
    return pages
      .map(p => {
        const hay = `${p.label} ${p.group || ''}`.toLowerCase();
        if (!words.every(w => hay.includes(w))) return null;
        // Rank: label starts with the first word > label contains it > only the group matched.
        const l = p.label.toLowerCase();
        const rank = l.startsWith(words[0]) ? 0 : l.includes(words[0]) ? 1 : 2;
        return { ...p, rank };
      })
      .filter(Boolean)
      .sort((a, b) => a.rank - b.rank);
  }, [q, pages]);

  useEffect(() => { setSel(0); }, [q]);
  useEffect(() => {
    const el = listRef.current?.children[sel];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  if (!open) return null;
  const go = (p) => { if (p) { onGo(p.path); onClose(); } };
  const onKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setSel(i => Math.min(results.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(i => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(results[sel]); }
  };

  return createPortal(
    <div style={st.overlay} onMouseDown={onClose}>
      <div style={st.box} onMouseDown={e => e.stopPropagation()} role="dialog" aria-label="Search pages">
        <div style={st.inputRow}>
          <Search size={18} style={{ color: '#86868B', flexShrink: 0 }} />
          <input
            ref={inputRef} value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey}
            placeholder="Search pages…  e.g. remittance, pickups, couriers" style={st.input}
          />
          <kbd style={st.kbd}>Esc</kbd>
        </div>
        <div ref={listRef} style={st.list}>
          {results.length === 0 && <div style={st.empty}>No page matches “{q}”</div>}
          {results.map((p, i) => (
            <button key={p.path} onMouseEnter={() => setSel(i)} onClick={() => go(p)}
              style={{ ...st.item, ...(i === sel ? st.itemOn : null) }}>
              <span style={{ ...st.ico, ...(i === sel ? st.icoOn : null) }}>{p.Icon && <p.Icon size={16} />}</span>
              <span style={{ flex: 1, textAlign: 'left' }}>
                <span style={{ color: '#1D1D1F', fontWeight: 500 }}>{p.label}</span>
                {p.group && <span style={{ color: '#86868B' }}>  ·  {p.group}</span>}
              </span>
              {i === sel && <CornerDownLeft size={14} style={{ color: '#86868B' }} />}
            </button>
          ))}
        </div>
        <div style={st.foot}><span><kbd style={st.kbdS}>↑</kbd><kbd style={st.kbdS}>↓</kbd> move</span><span><kbd style={st.kbdS}>Enter</kbd> open</span><span><kbd style={st.kbdS}>Ctrl</kbd>+<kbd style={st.kbdS}>K</kbd> anywhere</span></div>
      </div>
    </div>,
    document.body
  );
}

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif";
const st = {
  overlay: { position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.32)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '14vh', fontFamily: FONT },
  box: { width: 560, maxWidth: 'calc(100vw - 32px)', background: '#FFFFFF', borderRadius: 16, boxShadow: '0 24px 70px rgba(0,0,0,0.28), 0 0 0 1px rgba(0,0,0,0.06)', overflow: 'hidden' },
  inputRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px', borderBottom: '1px solid #EDEDF0' },
  input: { flex: 1, border: 'none', outline: 'none', fontSize: 16, color: '#1D1D1F', fontFamily: FONT, background: 'transparent' },
  kbd: { fontSize: 11, color: '#86868B', background: '#F2F2F5', borderRadius: 6, padding: '3px 7px', fontFamily: FONT },
  kbdS: { fontSize: 10.5, color: '#6E6E73', background: '#F2F2F5', borderRadius: 5, padding: '1px 6px', margin: '0 2px', fontFamily: FONT },
  list: { maxHeight: 360, overflowY: 'auto', padding: 6 },
  item: { width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px', border: 'none', background: 'transparent', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontFamily: FONT },
  itemOn: { background: '#F2F2F5' },
  ico: { width: 28, height: 28, borderRadius: 8, background: '#F2F2F5', color: '#6E6E73', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  icoOn: { background: '#111114', color: '#FFFFFF' },
  empty: { padding: '22px 14px', color: '#86868B', fontSize: 14, textAlign: 'center' },
  foot: { display: 'flex', gap: 18, padding: '10px 18px', borderTop: '1px solid #EDEDF0', color: '#86868B', fontSize: 12 },
};
